import type { DoubleMaterialityAssessmentData } from '@apps/materiality-tracker/api/materiality-assessment';
import { Chart } from 'react-google-charts';
import {
  ChartConfig,
  chartConfigs,
  DEFAULT_CHART_COLORS,
  getChartColumnColor,
  getChartEvents,
  LegendType,
  MATERIALITY_CHART_COLORS,
  renderDoubleMaterialityTooltip,
} from './chart-utils';
import './styles.scss';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { TableFooter } from '@g17eco/molecules/table/TableFooter';
import { PAGE_SIZE, usePagination } from '@hooks/usePagination';
import { useMemo, useState } from 'react';
import { ChartWrapperOptions } from 'react-google-charts/dist/types';
import { DoubleMaterialityChartLegend } from './partials/DoubleMaterialityChartLegend';

// --- Constants for Chart Synchronization ---
const CHART_WIDTH = '100%';
const CHART_HEIGHT = 600;
const BASE_OPTIONS: ChartWrapperOptions['options'] = {
  legend: 'none',
  bar: { groupWidth: '85%' },
  chartArea: {
    height: '100%',
    width: '100%',
  },
  vAxis: {
    textPosition: 'none',
  },
  hAxis: {
    textPosition: 'none',
    gridlines: { color: 'transparent' },
    baselineColor: 'none',
    viewWindow: { min: 0 },
    viewWindowMode: 'pretty',
  },
  annotations: {
    textStyle: {
      fontSize: 12,
      color: '#6a6c70',
    },
    highContrast: false,
    alwaysOutside: false,
  },
  tooltip: { isHtml: true, trigger: 'visible' },
};

const processChartData = ({
  currentData,
  config,
  category,
  selectedColumnIndex,
}: {
  currentData: DoubleMaterialityAssessmentData[];
  config: Pick<ChartConfig, 'code' | 'categories'> | undefined;
  category: LegendType;
  selectedColumnIndex: number;
}) => {
  return currentData.reduce(
    (acc, topic, index) => {
      if (!config) {
        return acc;
      }
      const { name, financialRelativeScore, nonFinancialRelativeScore } = topic;
      const color = getChartColumnColor(topic, config, category);
      acc.labels.push(name);
      acc.financialData.push([
        name,
        financialRelativeScore ?? NaN,
        financialRelativeScore ? `${financialRelativeScore.toFixed(2)}%` : '',
        `${color}; ${selectedColumnIndex === index ? 'opacity: 1' : 'opacity: 0.25'}`,
        renderDoubleMaterialityTooltip(topic),
      ]);
      acc.impactData.push([
        name,
        nonFinancialRelativeScore ?? NaN,
        nonFinancialRelativeScore ? `${nonFinancialRelativeScore.toFixed(2)}%` : '',
        `${color}; ${selectedColumnIndex === index ? 'opacity: 1' : 'opacity: 0.25'}`,
        renderDoubleMaterialityTooltip(topic),
      ]);
      return acc;
    },
    {
      labels: [],
      financialData: [
        [
          'Topic',
          'Financial',
          { role: 'annotation' },
          { role: 'style' },
          { type: 'string', role: 'tooltip', p: { html: true } },
        ],
      ],
      impactData: [
        [
          'Topic',
          'Impact',
          { role: 'annotation' },
          { role: 'style' },
          { type: 'string', role: 'tooltip', p: { html: true } },
        ],
      ],
    } as {
      labels: (string | undefined)[];
      financialData: (string | number | undefined | { [key: string]: any })[][];
      impactData: (string | number | undefined | { [key: string]: any })[][];
    },
  );
};

export const BiDirectionalBarChart = ({
  data,
  columnClickHandler,
  category,
}: {
  data: DoubleMaterialityAssessmentData[];
  columnClickHandler: (index: number) => void;
  category: LegendType;
}) => {
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(0);
  const config = chartConfigs.find((config) => config.code === category);
  const chartColors = category === LegendType.Materiality ? MATERIALITY_CHART_COLORS : DEFAULT_CHART_COLORS;

  const movePageCallback = (newPageIndex: number) => {
    columnClickHandler(newPageIndex * PAGE_SIZE);
    setSelectedColumnIndex(0);
  };

  const {
    handleMovePage,
    handleKeyUpPage,
    handleChangeCurrentPage,
    pageCount,
    pageIndex,
    currentPage,
    canPreviousPage,
    canNextPage,
    currentData,
  } = usePagination<DoubleMaterialityAssessmentData>({ data, movePageCallback });

  const { labels, financialData, impactData } = useMemo(
    () =>
      processChartData({
        currentData,
        config,
        category,
        selectedColumnIndex,
      }),
    [category, config, currentData, selectedColumnIndex],
  );

  if (!config) {
    return null;
  }

  const handleChartColumnClick = (columnIndex: number) => {
    const absoluteColumnIndex = pageIndex * PAGE_SIZE + columnIndex;
    setSelectedColumnIndex(columnIndex);
    return columnClickHandler(absoluteColumnIndex);
  };

  const chartEvents = getChartEvents(handleChartColumnClick);
  const rowHeight = CHART_HEIGHT / currentData.length;

  return (
    <div className='assessment__insights-chart' style={{ padding: '16px 0', width: CHART_WIDTH }}>
      <DoubleMaterialityChartLegend config={config} chartColors={chartColors} />
      <div className='d-flex text-ThemeHeadingMedium'>
        <div className='h4' style={{ width: '35%', textAlign: 'right' }}>
          Financial
        </div>
        <div style={{ width: '30%' }} />
        <div className='h4' style={{ width: '35%', textAlign: 'left' }}>
          Impact
        </div>
      </div>
      <div className='d-flex'>
        <div style={{ width: '35%' }}>
          <Chart
            chartType='BarChart'
            width={CHART_WIDTH}
            height={CHART_HEIGHT}
            data={financialData}
            chartEvents={chartEvents}
            options={{
              ...BASE_OPTIONS,
              // This is the key to making bars grow from right-to-left
              hAxis: {
                ...BASE_OPTIONS.hAxis,
                direction: -1,
              },
            }}
          />
        </div>
        <div className='d-flex flex-column' style={{ width: '30%' }}>
          {labels.map((label, index) => (
            <SimpleTooltip key={`${label}_${index}`} text={label}>
              <div className='d-flex align-items-center justify-content-center' style={{ height: rowHeight }}>
                <span style={{ maxWidth: 200 }} className='text-truncate text-ThemeTextMedium'>
                  {label}
                </span>
              </div>
            </SimpleTooltip>
          ))}
        </div>
        <div style={{ width: '35%' }}>
          <Chart
            chartType='BarChart'
            width={CHART_WIDTH}
            height={CHART_HEIGHT}
            data={impactData}
            chartEvents={chartEvents}
            options={BASE_OPTIONS}
          />
        </div>
      </div>
      <div className='pagination__wrapper'>
        <TableFooter
          pageCount={pageCount}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          currentPage={currentPage}
          handleMovePage={handleMovePage}
          handleChangeCurrentPage={handleChangeCurrentPage}
          handleKeyUpPage={handleKeyUpPage}
        />
      </div>
    </div>
  );
};
